import { ReactNode, VFC } from 'react'
import { useExhibitions } from 'src/api/exhibition'
import { useLoginUser } from 'src/api/user'
import { fontWeightBold } from 'src/styles/font'
import styled from 'styled-components'
import { ExhibitionCard } from './common'

import { useRouter } from 'next/router'
import {
  ExhibitionBranding,
  ExhibitionKeieisou,
  ExhibitionKeiri,
  ExhibitionMA,
  ExhibitionSetsuzei,
  ExhibitionShikin,
  ExhibitionSyain,
  ExhibitionSystem,
} from 'src/assets/svgs'
import { accentColor, white } from 'src/styles/colors'
import { searchExhibitions } from 'src/utils/routes'

export const CARD_IMAEGE_WIDTH = 272
export const CARD_IMAEGE_HEIGHT = 153

// -------------------------------------------------------------------------
//  ページコンポーネント
// -------------------------------------------------------------------------
export const ExhibitionPageLayout: VFC = () => {
  const router = useRouter()
  const { user } = useLoginUser()
  const { data } = useExhibitions()
  const exhibitions = data?.exhibitions ?? []
  const recommendExhibitions = data?.recommendedExhibitions ?? []
  console.log(recommendExhibitions)
  const genreCategories = [
    {
      name: '経営戦略・事業開発',
      icon: ExhibitionKeiri,
    },
    {
      name: 'ブランディング・マーケティング',
      icon: ExhibitionKeieisou,
    },
    {
      name: 'セールス強化・売上拡大',
      icon: ExhibitionShikin,
    },
    {
      name: '人材採用',
      icon: ExhibitionMA,
    },
    {
      name: '人・組織づくり',
      icon: ExhibitionSyain,
    },
    {
      name: '業務改善・システム開発・デジタル化',
      icon: ExhibitionSystem,
    },
    {
      name: '財務・資金管理・バックオフィス',
      icon: ExhibitionSetsuzei,
    },
    {
      name: 'その他',
      icon: ExhibitionBranding,
    },
  ]

  const SectionHeader = ({
    titleJa,
    titleEn,
    description,
  }: {
    titleJa: string
    titleEn: string
    description: ReactNode
  }) => (
    <SectionHeaderContainer>
      <SectionHeaderLeft>
        <SectionHeaderBar />
        <SectionHeaderTitles>
          <SectionHeaderTitleJa>{titleJa}</SectionHeaderTitleJa>
          <SectionHeaderTitleEn>{titleEn}</SectionHeaderTitleEn>
        </SectionHeaderTitles>
      </SectionHeaderLeft>
      <SectionHeaderDescription>{description}</SectionHeaderDescription>
    </SectionHeaderContainer>
  )

  return (
    <>
      <Container>
        <Wrapper>
          <PageColumn>
            <SectionHeader
              titleJa="オンライン展示会"
              titleEn="Online Exhibition"
              description={
                <>
                  最新の製品・サービスをいつでもどこでもご覧いただけるオンライン展示会です。
                  <br />
                  気になるブースを自由に回って、情報収集やお問い合わせが可能です。
                </>
              }
            />

            <CategorySection>
              <SectionHeader
                titleJa="展示カテゴリー"
                titleEn="Category"
                description=""
              />
              <CategoryGrid>
                {genreCategories.map((category, index) => (
                  <CategoryButton
                    key={index}
                    onClick={() => {
                      router.push(searchExhibitions(category.name))
                    }}
                  >
                    <CategoryIconWrapper>
                      <category.icon />
                    </CategoryIconWrapper>
                    <CategoryName>{category.name}</CategoryName>
                  </CategoryButton>
                ))}
              </CategoryGrid>
            </CategorySection>

            <RecommendSection>
              <SectionHeader
                titleJa="注目のサービス"
                titleEn="Recommend"
                description=""
              />
              <ExhibitionGrid>
                {recommendExhibitions.map((e) => (
                  <ExhibitionCard
                    key={e.id}
                    exhibition={e}
                    cardHeight={500}
                    cardWidth={480}
                    imageHeight={CARD_IMAEGE_HEIGHT}
                    imageWidth={CARD_IMAEGE_WIDTH}
                    loggedIn={!!user}
                  />
                ))}
              </ExhibitionGrid>
              <ButtonWrapper>
                <ShowMoreButton href="/member/exhibitions/search?filterBy=recommend">
                  もっと見る
                </ShowMoreButton>
              </ButtonWrapper>
            </RecommendSection>

            <NewSection>
              <SectionHeader
                titleJa="新着サービス"
                titleEn="New"
                description=""
              />
              <ExhibitionGrid>
                {exhibitions.slice(0, 6).map((e) => (
                  <ExhibitionCard
                    key={e.id}
                    exhibition={e}
                    cardHeight={500}
                    cardWidth={480}
                    imageHeight={CARD_IMAEGE_HEIGHT}
                    imageWidth={CARD_IMAEGE_WIDTH}
                  />
                ))}
              </ExhibitionGrid>
              <ButtonWrapper>
                <ShowMoreButton href="/member/exhibitions/search?orderBy=recent">
                  もっと見る
                </ShowMoreButton>
              </ButtonWrapper>
            </NewSection>
          </PageColumn>
        </Wrapper>
      </Container>
    </>
  )
}

// -------------------------------------------------------------------------
//  styled‑components
// -------------------------------------------------------------------------
const Container = styled.div`
  position: relative;
`
const Wrapper = styled.main`
  max-width: 1050px;
  margin: 0 auto;
  padding: 40px 20px 120px;
`
const PageColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 80px;
`

// SectionHeader用のスタイル
const SectionHeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`

const SectionHeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const SectionHeaderBar = styled.div`
  width: 3px;
  height: auto;
  align-self: stretch;
  background-color: #a5bfbe;
  border-radius: 4px;
`

const SectionHeaderTitles = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`

const SectionHeaderTitleJa = styled.h2`
  font-family: 'Noto Sans JP', Helvetica, sans-serif;
  font-weight: ${fontWeightBold};
  color: #333333;
  font-size: 20px;
  line-height: normal;
  white-space: nowrap;
`

const SectionHeaderTitleEn = styled.p`
  font-family: 'Lato', Helvetica, sans-serif;
  font-weight: 300;
  color: #808080;
  font-size: 14px;
  line-height: normal;
`

const SectionHeaderDescription = styled.p`
  flex: 1;
  font-family: 'Noto Sans JP', Helvetica, sans-serif;
  font-weight: 400;
  color: #606060;
  font-size: 14px;
  line-height: 22.4px;
`

const CategorySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

// カテゴリーグリッド用のスタイル
const CategoryGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  width: 100%;
  margin-bottom: 32px;
`

const CategoryButton = styled.button`
  display: flex;
  width: 327px;
  align-items: center;
  gap: 4px;
  padding: 6px 4px;
  background-color: #02b7b0;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #029a94;
  }
`

const CategoryIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`

const CategoryName = styled.span`
  font-family: 'Noto Sans JP', Helvetica, sans-serif;
  font-weight: ${fontWeightBold};
  color: #ffffff;
  font-size: 16px;
  line-height: normal;
  white-space: nowrap;
`

export const ExhibitionLogoDiv = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.8);
  padding: 4px;
  width: 219px;
  font-weight: ${fontWeightBold};
  align-items: center;

  img {
    margin-right: 8px;
    height: 52px;
  }
  h2 {
    font-size: 27px;
  }
  h3 {
    font-size: 18px;
  }
  margin-bottom: 20px;
`
const RecommendSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 32px;
`
const NewSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 32px;
`

const ExhibitionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`

const ShowMoreButton = styled.a`
  display: inline-block;
  width: 232px;
  font-size: 16px;
  line-height: 1;
  font-weight: ${fontWeightBold};
  color: ${accentColor};
  border-radius: 5px;
  border: 1px solid ${accentColor};
  text-align: center;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  text-decoration: none;

  &:hover {
    background-color: ${accentColor};
    color: ${white};
    text-decoration: none;
  }
`
